const MAX_PREVIEW_EDGE_PX = 1080;
const PREVIEW_JPEG_QUALITY = 0.9;

type PdfViewport = { width: number; height: number };

type PdfPage = {
  getViewport: (options: { scale: number }) => PdfViewport;
  render: (options: {
    canvasContext: CanvasRenderingContext2D;
    viewport: PdfViewport;
  }) => { promise: Promise<void> };
};

type PdfDocument = {
  getPage: (pageNumber: number) => Promise<PdfPage>;
};

// images/upload caps files at 5MB; a full-size PNG of a photo-heavy carousel page exceeds it.
export const renderFirstPagePreview = async (
  pdfFile: PdfDocument
): Promise<Blob> => {
  const page = await pdfFile.getPage(1);
  const { width, height } = page.getViewport({ scale: 1 });
  const scale = Math.min(1.5, MAX_PREVIEW_EDGE_PX / Math.max(width, height));
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not render the pdf preview");
  }
  canvas.height = viewport.height;
  canvas.width = viewport.width;
  // Pages with transparency groups render unfilled, which JPEG encodes as black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: context, viewport }).promise;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob =>
        blob
          ? resolve(blob)
          : reject(new Error("Could not render the pdf preview")),
      "image/jpeg",
      PREVIEW_JPEG_QUALITY
    );
  });
};
